import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../../lib/api.js';
import { useA2pBypass } from '../../../lib/a2pBypassFlag.js';
import { useAuth } from '../../../store/auth.js';
import toast from 'react-hot-toast';

/**
 * State machine + side effects for the 5-step number purchase wizard.
 * Owns step/search/selection state, the search + purchase API calls and wallet/agreement gating.
 * @returns {Object} Wizard state and handlers consumed by NumbersWizard.
 */
export default function useNumbersWizard() {
  const nav = useNavigate();
  const { agent, token, setSession } = useAuth();
  const { hideUI: hideA2pUI, bypassEnabled } = useA2pBypass();
  const isParentBilled = hideA2pUI || bypassEnabled;
  // Funding step shows until a card is on file AND the wallet can cover a $1.00 line.
  const needsWallet = agent?.role === 'agent' && (!agent?.hasPaymentMethodOnFile || (agent?.walletBalance || 0) < 1.00);
  const [step, setStep] = useState(1);
  const [numberType, setNumberType] = useState('local');
  // Ringy-style search filter mode. `areaCode` = single 3-digit US area code
  // (legacy behavior). `states` = one or more US state codes; the backend fans
  // out per-state Twilio queries and merges. When both are supplied the
  // states are the primary filter and areaCode narrows within them.
  const [searchMode, setSearchMode] = useState('areaCode');
  const [areaCode, setAreaCode] = useState('');
  const [states, setStates] = useState([]); // 2-letter US codes
  const [results, setResults] = useState([]);
  // True when /phones/search served mock inventory (dev, no Twilio creds).
  const [simulated, setSimulated] = useState(false);
  // Multi-select: which phoneNumbers the user has picked for purchase. We
  // keep the raw result records under `selectedRecords` so downstream steps
  // can render friendlyName/locality without another lookup.
  const [selectedNumbers, setSelectedNumbers] = useState(new Set());
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  // Single-number result (legacy shape) OR bulk result. `purchased` holds a
  // single trusted PhoneNumber for the count=1 path; `bulkResult` holds the
  // per-number results array + summary for count>1.
  const [purchased, setPurchased] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmCancel, setConfirmCancel] = useState(false);
  // Toll-Free is a voice-only, separate flow. Selecting it prompts a
  // confirmation before routing to /phone-setup/toll-free (PR #429B).
  const [confirmTollFree, setConfirmTollFree] = useState(false);

  const { data: ownedData, refetch: refetchOwned } = useQuery({
    queryKey: ['phones-owned'],
    queryFn: () => apiGet('/phones').catch(() => ({ numbers: [] })),
  });
  const owned = ownedData?.numbers || [];

  // PR #429 — a direct producer must sign the Direct Producer Agreement before
  // buying numbers (the backend refuses with producer_agreement_not_signed).
  const { data: agreementData, refetch: refetchAgreement } = useQuery({
    queryKey: ['producer-agreement'],
    queryFn: () => apiGet('/onboarding/producer-agreement').catch(() => null),
    staleTime: 30_000,
  });
  const agreementSigned = agreementData ? !!agreementData.alreadySigned : true;

  function reset() {
    setStep(1);
    setNumberType('local');
    setSearchMode('areaCode');
    setAreaCode('');
    setStates([]);
    setResults([]);
    setSimulated(false);
    setSelectedNumbers(new Set());
    setPurchased(null);
    setBulkResult(null);
    setFieldErrors({});
    setConfirmCancel(false);
  }

  // Records for the numbers currently selected (derived from the search
  // results). Used by the purchase and summary steps to render friendly
  // metadata without a lookup.
  const selectedRecords = results.filter((n) => selectedNumbers.has(n.phoneNumber));

  async function runSearch() {
    setFieldErrors({});
    if (numberType === 'local') {
      // Client-side validation mirrors the server contract exactly so users
      // see errors inline instead of via a 422 round-trip.
      if (searchMode === 'areaCode' && !/^\d{3}$/.test(areaCode)) {
        setFieldErrors({ areaCode: 'Enter a 3-digit US area code.' });
        return;
      }
      if (searchMode === 'states' && states.length === 0) {
        setFieldErrors({ states: 'Pick at least one state to search.' });
        return;
      }
    }
    setSearching(true);
    try {
      // Fetch a wide pool (100) so users can pick up to the 50-number bulk cap
      // and still have headroom to deselect a few. Backend clamps to 100.
      const params = { numberType, limit: 100 };
      // Outside production always ask for safe mock inventory (555-01xx) so a
      // stray click can never buy a real DID. The server also enforces this.
      if (!import.meta.env.PROD) params.simulated = true;
      if (numberType === 'local') {
        if (searchMode === 'areaCode' || (searchMode === 'states' && areaCode)) {
          if (/^\d{3}$/.test(areaCode)) params.areaCode = areaCode;
        }
        if (searchMode === 'states' && states.length > 0) {
          params.states = states.join(',');
        }
      }
      const r = await apiGet('/phones/search', params);
      setResults(r.numbers || []);
      setSimulated(Boolean(r.simulated));
      setSelectedNumbers(new Set());
      if (!r.numbers?.length) {
        const msg = searchMode === 'states'
          ? 'No numbers available in the selected states. Try different states or add an area code.'
          : 'No numbers available for that area code. Try another.';
        setFieldErrors(searchMode === 'states' ? { states: msg } : { areaCode: msg });
        return;
      }
      setStep(3);
    } catch (e) {
      const data = e.response?.data || {};
      if (data.fieldErrors) setFieldErrors(data.fieldErrors);
      else if (!e.response) toast.error(data.error || e.message || 'Search failed.');
      else setFieldErrors({ areaCode: data.error || 'Search failed.' });
    } finally {
      setSearching(false);
    }
  }

  async function runPurchase() {
    const picks = selectedRecords;
    if (picks.length === 0) return;
    setFieldErrors({});
    setPurchasing(true);
    try {
      if (picks.length === 1) {
        // Single purchase preserves the legacy trust-chain polling UX. We
        // deliberately keep hitting POST /phones/purchase (not /purchase-bulk)
        // so the response carries the rich provisioning payload the StepProvisioning
        // polling panel expects to seed itself from.
        // Ship locality/region so the Active Numbers table can render city/state
        // without the server needing to re-query Twilio per row.
        const r = await apiPost('/phones/purchase', {
          phoneNumber: picks[0].phoneNumber,
          locality: picks[0].locality || '',
          region: picks[0].region || '',
        });
        setPurchased({ ...r.phoneNumber, ...r });
        setBulkResult(null);
        toast.success('Line activated successfully!');

        // Deduct $1.00 from local wallet state and complete onboarding
        if (agent) {
          const updated = {
            ...agent,
            walletBalance: Math.max(0, (agent.walletBalance || 0) - 1.00),
            hasCompletedOnboarding: true,
          };
          setSession(token, updated);
        }
      } else {
        // Send objects (not raw strings) so the bulk endpoint can persist
        // city/state on every PhoneNumber doc for the Active Numbers table.
        const r = await apiPost('/phones/purchase-bulk', {
          phoneNumbers: picks.map((n) => ({
            phoneNumber: n.phoneNumber,
            locality: n.locality || '',
            region: n.region || '',
          })),
        });
        setBulkResult(r);
        setPurchased(null);

        if (agent) {
          const chargedCount = r.summary?.purchased || 0;
          const updated = {
            ...agent,
            walletBalance: Math.max(0, (agent.walletBalance || 0) - chargedCount * 1.00),
            hasCompletedOnboarding: true,
          };
          setSession(token, updated);
        }

        if (r.summary?.failed > 0) {
          toast.error(`${r.summary.failed} of ${r.summary.requested} purchases failed — see the summary below.`);
        }
      }
      // step 5 = post-purchase: StepProvisioning if `purchased`, StepBulkSummary if `bulkResult`.
      setStep(5);
      refetchOwned();
    } catch (e) {
      const data = e.response?.data || {};
      if (data.fieldErrors) {
        setFieldErrors(data.fieldErrors);
      } else if (!e.response) {
        toast.error(e.message || data.error || 'Purchase failed — please try again.');
      } else {
        // Trust step failed OR whole-batch preflight (agent_cap_overrun,
        // campaign_capacity_insufficient) blocked the buy — nothing charged.
        const rolledBack = data.reason === 'messaging_attach_failed' || data.reason === 'shaken_attest_failed';
        setFieldErrors({ _form: data.error || e.message || 'Purchase failed.' });
        toast.error(
          rolledBack
            ? `${data.error || 'Trust registration failed'} — number released, no charge.`
            : (data.error || e.message || 'Purchase failed.'),
        );
      }
    } finally {
      setPurchasing(false);
    }
  }

  function goSettings() { nav('/settings?section=phone'); }

  function requestCancel() {
    if (step === 1 || (selectedNumbers.size === 0 && !purchased && !bulkResult)) { reset(); return; }
    setConfirmCancel(true);
  }

  return {
    nav,
    agent,
    isParentBilled,
    needsWallet,
    step,
    setStep,
    numberType,
    setNumberType,
    searchMode,
    setSearchMode,
    areaCode,
    setAreaCode,
    states,
    setStates,
    results,
    simulated,
    selectedNumbers,
    setSelectedNumbers,
    searching,
    purchasing,
    purchased,
    bulkResult,
    fieldErrors,
    confirmCancel,
    setConfirmCancel,
    confirmTollFree,
    setConfirmTollFree,
    owned,
    refetchOwned,
    agreementSigned,
    agreementData,
    refetchAgreement,
    reset,
    selectedRecords,
    runSearch,
    runPurchase,
    goSettings,
    requestCancel,
  };
}
